import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  UserContext,
  OrganisationContext,
  ReportsDownloadUpdate,
} from "../../layouts/MainLayout";
import {
  reportsTypesByRoutes,
  tableHeading,
  sortList,
  reportTypeString,
} from "./ReportsDetailsConstants";
import moment from "moment-timezone";

/**Components**/
import Icons from "../../utils/components/icons";
import Pagination from "../../utils/components/common/pagination";
import IndividualReport from "./Reports/IndividualReport";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import SearchBox from "../../utils/components/common/searchBox/deviceSearchBox";
import Modal from "../../utils/components/common/modal";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import { formatAMPM, handleTableColSort } from "../../utils/utils";

/**Service Methods**/
import {
  auditTrailReportdata,
  generateAuditTrailCSVReport,
} from "../../services/ReportService";

/**Styling**/
import "./ReportDetails.scss";
import AuditTrailFilter from "./reportFilters/AuditTrailFilter";
import classNames from "classnames";

/**Component Function*/
const ReportsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation("reports");

  const { userCognitoData } = useContext(UserContext);
  const { siteData, orgData } = useContext(OrganisationContext);
  const { reportUpdateStatus } = useContext(ReportsDownloadUpdate);
  const searchBoxRef = useRef();

  const currentReportType = useMemo(() => {
    const currentRoute = location?.pathname?.split("/")[2] || "";
    if (!currentRoute) return "";
    return reportsTypesByRoutes[currentRoute];
  }, [location]);

  const newDownloadNotification = useMemo(() => {
    const data = reportUpdateStatus.find(
      (val) => val.reportType === reportTypeString[currentReportType]
    );
    return !!data;
  }, [currentReportType, reportUpdateStatus]);

  const [reportsData, setReportsData] = useState();
  const [selectedDevice, setSelectedDevice] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [showErr, setShowErr] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [currentReportFilter, setCurrentReportFilter] = useState({});
  const [refreshTime, setRefreshTime] = useState(new Date());
  const [sortColList, setSortColList] = useState(
    currentReportType && sortList[currentReportType]
  );
  const [selectedSortList, setSelectedSortList] = useState({});

  const [currentTableHeading, setCurrentTableHeading] = useState(
    tableHeading[currentReportType]
  );

  const [durationErr, setDurationErr] = useState({ isErr: false, errMsg: "" });
  const [selectedAllSites, setSelectedAllSites] = useState(false);

  /**PaginationVariables Starts**/
  const [pageTotalCount, setPageTotalCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  /**PaginationVariables Ends**/

  /**API call for Audit Trail report**/
  const getAuditTrailReportdata = useCallback((queryParams) => {
    auditTrailReportdata(queryParams)
      .then((response) => {
        const data = response.data.items;
        setReportsData(data);
        setRefreshTime(new Date());
        setPageTotalCount(Math.ceil(response.data.totalCount / recordsPerPage));
        setIsLoading(false);
        setShowErr(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setShowErr(true);
      });
  }, []);

  /**Get Updated data as per current report type**/
  useEffect(() => {
    if (
      !(
        siteData.id &&
        userCognitoData?.username &&
        userCognitoData?.signInUserSession &&
        orgData?.TenantId &&
        !durationErr.isErr
      )
    ) {
      setReportsData();
      return;
    }
    setIsLoading(true);

    let queryParams = {};
    const filters = currentReportFilter;
    delete filters.durationErr;
    filters.sites = [siteData.id];

    /**
     * For below conversion:- Need to updated the sort structure.
     * from: inputObject = {timeStamp: "desc",mode: "asc",clo2Value: "asc",....}
     * to [{"timeStamp": "desc"},{"mode": "asc"},{"clo2Value": "asc"}....so on]
     */
    const sortList = Object.entries(selectedSortList).map(([key, value]) => ({
      [key]: value,
    }));

    queryParams = {
      requestedUserId:
        userCognitoData?.signInUserSession?.accessToken?.payload?.username,
      tenantId: orgData?.TenantId,
      size: recordsPerPage,
      pageNo: currentPage,
      filters: filters,
      sort: sortList,
    };

    if (currentReportType === "auditTrail") {
      const newTimeStamp = queryParams?.filters?.timeStamp;
      let timestamp1;
      let timestamp2;

      if (newTimeStamp) {
        timestamp1 = new Date(newTimeStamp?.between[0]);
        timestamp2 = new Date(newTimeStamp?.between[1]);

        if (timestamp1 < timestamp2) {
          getAuditTrailReportdata(queryParams);
        }
      }

      if (queryParams?.sort[0]?.timeStamp === "ascnoSort") {
        queryParams.sort[0].timeStamp = "asc";
      }
    }
  }, [
    currentReportType,
    orgData,
    selectedDevice,
    siteData,
    userCognitoData?.username,
    selectedSortList,
    currentReportFilter,
    currentPage,
    userCognitoData?.signInUserSession,
    durationErr.isErr,
    getAuditTrailReportdata,
  ]);

  const handlePageChange = useCallback((item) => {
    setCurrentPage(item + 1);
  }, []);

  /**Go back to device view**/
  const goBack = useCallback(() => {
    navigate("/reports");
  }, [navigate]);

  /**Sort Handler**/
  const updateSortingList = useCallback(
    (key, sortOrder = "") => {
      if (currentReportType !== "auditTrail") {
        if (Object.keys(sortColList).indexOf(key) === -1) return;
      }

      let newOrder = "asc";
      if (sortOrder) {
        newOrder = sortOrder;
      } else {
        if (currentReportType === "auditTrail") {
          if (sortColList[key] === "asc") {
            newOrder = "desc";
          } 
          // else if (sortColList[key] === "ascnoSort") {
          //   newOrder = "asc";
          // }
           else if (sortColList[key] === "desc") {
            newOrder = "asc";
          }
        } else {
          newOrder = sortColList[key] === "asc" ? "desc" : "";
        }
      }

      setSortColList((prevState) => {
        return { ...sortList[currentReportType], [key]: newOrder };
      });
      setSelectedSortList(() => {
        return { [key]: newOrder };
      });
      setCurrentPage(1);
    },
    [currentReportType, sortColList]
  );

  useEffect(() => {
    if (currentReportType === "auditTrail") {
      updateSortingList("timeStamp", "desc");
    }
  }, []);

  const sortLabel = {
    asc: "Ascending sort",
    desc: "Descending sort",
    ascnoSort: "No sort",
  };

  /**Filter Handler**/
  const updateFilterValues = useCallback((filterVal) => {
    setCurrentReportFilter((prevValues) => {
      return { ...filterVal };
    });
    setCurrentPage(1);
  }, []);

  /**Update the filterState for device */
  const updateSelectedDevice = useCallback(() => {
    const deviceList = searchBoxRef.current.getSelectedList();
    const deviceIdList = deviceList.map((list) => {
      const id = list.PK.slice(2);
      return id;
    });
    setSelectedDevice(deviceIdList.length ? deviceIdList : undefined); //Add above deviceIdList once search filter code is done

    if (currentReportType === "auditTrail") {
      if (selectedAllSites) {
        setCurrentTableHeading(tableHeading.auditTrailSite);
      } else {
        setCurrentTableHeading(tableHeading.auditTrail);
      }
    }
  }, [currentReportType, selectedAllSites]);

  /**Generate event on csv click */
  const onCsvClick = useCallback(() => {
    setIsLoading(true);
    let queryParam;

    if (currentReportType === "auditTrail") {
      const filters = currentReportFilter;
      filters.sites = [siteData.id];

      const sortList = Object.entries(selectedSortList).map(([key, value]) => ({
        [key]: value,
      }));

      queryParam = {
        requestedUserId: userCognitoData?.username,
        tenantId: orgData?.TenantId,
        filters: filters,
        sort: [sortList[0]],
        userTimezone: moment.tz.guess(),
      };

      if (queryParam?.sort[0]?.timeStamp === "ascnoSort") {
        queryParam.sort[0].timeStamp = "asc";
      }

      generateAuditTrailCSVReport(queryParam)
        .then((response) => {
          setIsLoading(false);
          setShowDownloadModal(true);
        })
        .catch((e) => {
          setIsLoading(false);
        });
    }
  }, [
    currentReportFilter,
    currentReportType,
    orgData?.TenantId,
    selectedSortList,
    siteData.id,
    userCognitoData?.username,
  ]);

  const emptyMsg = useMemo(() => {
    if (durationErr.isErr) return durationErr.errMsg;

    if (!selectedDevice) return t(`noDataMsg.${currentReportType}`);

    if (showErr) return t("errMsg1");

    if (!isLoading && !reportsData?.length && selectedDevice && !showErr)
      return t("no-result");

    return "";
  }, [
    selectedDevice,
    t,
    currentReportType,
    showErr,
    isLoading,
    reportsData?.length,
    durationErr,
  ]);

  const updateDurationErr = useCallback((errObj) => {
    setDurationErr(errObj);
  }, []);

  /**JSX**/
  return (
    <div className="bodyInner reports-details-page">
      <div className={`bodyContainer`}>
        {/* Header Section Starts */}
        <div className="controllesWrapper fixSubHeader">
          {/* Header's Left-Section :- Heading */}
          <div className="page-heading">
            <span className="back-to-device pointer heading-1" onClick={goBack}>
              {t("reportsHeading")}
            </span>
            <span className="heading-2">
              / {t(`${currentReportType}Heading`)}
            </span>
          </div>
          {/* Header's Right-Section :- Other options */}
          <div className="d-flex align-items-center right-header">
            <div>
              {currentReportType !== "auditTrail" && (
                <SearchBox
                  ref={searchBoxRef}
                  autoid="report-search-device"
                  placeholderText={t("deviceSearch.placeholder")}
                  titleText={t("deviceSearch.title")}
                  getOptionCallBack={() => {
                    updateSelectedDevice();
                  }}
                  isMultiSelect={currentReportType === "deviceDiagnostics"}
                />
              )}
            </div>
            <span className="enabledIcon">
              <button
                className={
                  newDownloadNotification
                    ? "verticalBtn"
                    : "hide-circle verticalBtn"
                }
                onClick={() => navigate("/reports/downloads")}
                title={t("downloads")}
              >
                {Icons.downalodStatusIcon}
              </button>
            </span>
            <span className="disabledIcon">
              <button
                className={"verticalBtn"}
                disabled
                title={t("mark_favorite")}
              >
                {Icons.favoriteIcon}
              </button>
            </span>
            <span className="disabledIcon">
              <button
                className="verticalBtn"
                disabled
                title={t("export_as_pdf")}
              >
                {Icons.pdfIcon}
              </button>
            </span>
            <span
              className={
                reportsData?.length ||
                (currentReportType === "deviceGraph" && reportsData)
                  ? "enabledIcon"
                  : "disabledIcon"
              }
            >
              <button
                className="verticalBtn"
                onClick={onCsvClick}
                disabled={
                  (currentReportType === "deviceGraph" && !reportsData) ||
                  (currentReportType !== "deviceGraph" && !reportsData?.length)
                }
                title={
                  currentReportType === "deviceGraph"
                    ? t("export_graph_csv")
                    : t("export_as_csv")
                }
              >
                {Icons.csvIcon}
              </button>
            </span>
            <span className="disabledIcon">
              <button className="verticalBtn" disabled title={t("print")}>
                {Icons.printIcon}
              </button>
            </span>
          </div>
        </div>
        {/* Header Section Ends */}
        {/* Filter Section Starts */}
        <div className="filters-container fixSubHeader">
          <AuditTrailFilter
            updateDurationErr={updateDurationErr}
            updateFilterValues={updateFilterValues}
            currentReportType={currentReportType}
          />
        </div>
        {/* Filter Section Ends */}
        {/* Table Section Starts */}
        <div className="gridViewWrapper">
          <div className="gridViewInner">
            <table
              className={`table table-striped ${currentReportType}`}
              id="reports-table"
            >
              <thead>
                <tr>
                  {currentTableHeading.map((heading) => {
                    return (
                      <th
                        key={heading}
                        title={t(
                          `tableHeading.${currentReportType}.${heading}`
                        )}
                        onClick={() => {
                          reportsData && updateSortingList(heading);
                        }}
                      >
                        <div id="listheader" className="sortIconWrapper">
                          <span className="clipText1">
                            {t(`tableHeading.${currentReportType}.${heading}`)}
                          </span>

                          {Object.keys(sortColList).indexOf(heading) !== -1 ? (
                            <div
                              className="sortIcons"
                              title={sortLabel[sortColList[heading]]}
                            >
                              <button
                                type="button"
                                id="sort_asc"
                                className={classNames({
                                  active: sortColList[heading] === "asc",
                                  ascbutton: "ascbutton",
                                })}
                              >
                                {Icons.upArrowIcon}
                              </button>
                              <button
                                type="button"
                                id="sort_desc"
                                className={classNames({
                                  active: sortColList[heading] === "desc",
                                })}
                              >
                                {Icons.downArrowIcon}
                              </button>
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {/* Table data starts */}
              {/* {selectedDevice && reportsData ? ( */}
              {reportsData?.length ? (
                <>
                  <IndividualReport
                    currentReportType={currentReportType}
                    reportData={reportsData}
                  />
                </>
              ) : (
                <></>
              )}
              {/* Empty Screen Starts */}
              {/* Emprty screen:- In case of no-device or error Starts */}
              {currentReportType !== "auditTrail" ? (
                <>
                  {(!selectedDevice || showErr) && (
                    <tbody>
                      <tr className="select-device-msg-tr">
                        <td colSpan={currentTableHeading.length}>
                          <EmptyScreen
                            img={Icons.emptyScreenReports}
                            title={t("no_data_to_display")}
                            desc={emptyMsg}
                          />
                        </td>
                      </tr>
                    </tbody>
                  )}
                </>
              ) : (
                <>
                  {(showErr || !reportsData?.length) && (
                    <tbody>
                      <tr className="select-device-msg-tr">
                        <td colSpan={currentTableHeading.length}>
                          <EmptyScreen
                            img={Icons.emptyScreenReports}
                            title={t("no_data_to_display")}
                            desc=""
                          />
                        </td>
                      </tr>
                    </tbody>
                  )}
                </>
              )}

              {/* Emprty screen:- In case of no-device or error Ends */}
              {/* Emprty screen:- In case of no data found Starts */}

              {!isLoading &&
                !reportsData?.length &&
                selectedDevice &&
                !showErr && (
                  <tbody>
                    <tr className="select-device-msg-tr">
                      <td colSpan={currentTableHeading.length}>
                        <EmptyScreen
                          img={Icons.emptyScreenReports}
                          title="No Data to display!"
                          desc={emptyMsg}
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
              {/* Emprty screen:- In case of no data found ends */}

              {/* Empty Screen Ends */}

              {/* Table data ends */}
            </table>
          </div>
        </div>
        {/* Table Section Ends */}
        {isLoading && <LoadingSpinner />}
        {/* Pagination Section Starts */}
        <div className="bodyFooter footerFixed">
          {reportsData && (
            <>
              {currentReportType !== "deviceGraph" ? (
                <>
                  <Pagination
                    currentPage={currentPage - 1}
                    totalPages={pageTotalCount}
                    pageCallBack={handlePageChange}
                  />
                </>
              ) : (
                <></>
              )}
              <div id="" className="footer-time">
              <span className="text-h4">
              {t("reportGeneratedOn", {
                      time: formatAMPM(refreshTime, false),
                    })}{" "}
              </span>
              </div>
            </>
          )}
        </div>
        {/* Pagination Section Ends */}
        {/* Modal Section starts */}
        <Modal
          idName="go-to-download"
          isOpen={showDownloadModal}
          onCloseModal={() => setShowDownloadModal(false)}
          isCloseButton
        >
          <p className="fs-5 mt-3 text-center" id="cancel_confirm_text">
            {t("downloadModalMsg")}
          </p>
          <div className="fluid d-flex justify-content-center mb-4">
            <button
              className="roundBtn fs-6 mx-2"
              id="confirm_cancel"
              onClick={() => setShowDownloadModal(false)}
            >
              <span>{t("Close")}</span>
            </button>
            <button
              className="roundBtn primary fs-6 mx-2"
              id="cancel_the_cancel_task"
              type="button"
              onClick={() => navigate("/reports/downloads")}
            >
              <span>{t("Go to Downloads")}</span>
              {Icons.maximizeIcon}
            </button>
          </div>
        </Modal>
        {/* Modal Section Ends */}
      </div>
    </div>
  );
};

export default ReportsDetails;
